import { Tags } from "@/enum/tags";
import type { SchedulerSummary } from "@/pages/tax/extracao-nf-ofm/dtos/ofm-dtos";
import {
  blobDownloadResponseHandler,
  type BlobDownload,
  type EmptyResponse,
} from "@/utils/blob-response-handler";
import { apiSlice } from "./http";

export interface CreateTaxClosingRequest {
  stores: number[];
  month: number;
  year: number;
}
export interface CreateTaxClosingResponse {
  jobId: string;
}

export type TaxClosingJobStep =
  | "queued"
  | "fetching_cnpjs"
  | "scheduling_stores"
  | "polling_stores"
  | "calculating"
  | "var_lookup"
  | "aggregating"
  | "generating_excel"
  | "caching"
  | "done";

export type TaxClosingStoreJobStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "skipped";

export interface TaxClosingStoreJobState {
  code: number;
  cnpj: string | null;
  status: TaxClosingStoreJobStatus;
  noteCount?: number;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface TaxClosingJobProgress {
  step: TaxClosingJobStep;
  pct: number;
  totalStores: number;
  stores: TaxClosingStoreJobState[];
  currentStoreIndex: number | null;
  successCount: number;
  failedCount: number;
  message: string;
}

export interface JobStatusResponse {
  jobId: string;
  status: "waiting" | "active" | "completed" | "failed" | "delayed" | "paused";
  progress: number;
  progressDetail: TaxClosingJobProgress | null;
  returnvalue: any | null;
  failedReason: string | null;
}

export interface ActiveJobResponse {
  hasActiveJob: boolean;
  jobId?: string;
  status?: string;
  progress?: number;
}

export interface VarRow {
  loja: number;
  serie: number;
  nfce: number;
  contingencia: number;
  data: string;
  hora: string;
  status: "Producao" | "Cancelada";
  valorContabil: string;
  chave: string;
  protocolo: string | null;
  vendaFechada: "Sim" | "Nao";
  docCod: number;
  cupom: number;
  valorDesconto: string;
  valorContabilGft: string;
  dif: string;
  matched: boolean;
  notComparableReason: string | null;
}

export interface VarTabData {
  available: boolean;
  message?: string;
  rows: VarRow[];
  summary: {
    totalRows: number;
    totalDif: string;
    rowsWithoutMatch: number;
    rowsInContingency: number;
    rowsWithDivergence: number;
    rowsNotComparable: number;
  };
}
export interface TaxClosingTabs {
  conferencias: { rowCount: number };
  modeloCfopCst: {
    modeloCfop: any[];
    cstTributacao: any[];
    cstTributacaoTotal: any;
    ufCfop: any[];
  };
  dinamicaVsLivro: {
    groups: any[];
    grandTotal: any;
  };
  var: VarTabData & { rowCount: number };
  summary: {
    totalNotes: number;
    totalStores: number;
    totalNovaBaseIcms: string;
    divergenceCount: number;
  };
}

export type RowFilters = Record<string, string[]>;

const FILTERS_QUERY_KEY = "filters";

export interface TabRowsParams {
  jobId: string;
  tab: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
  search?: string;
  filters?: RowFilters;
}

export interface TabRowsMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  totalFiltered: number;
}

export interface TabRowsResponse<T = Record<string, unknown>> {
  rows: T[];
  meta: TabRowsMeta;
}

export interface ProcessedDataResponse {
  jobId: string;
  data: TaxClosingTabs;
  facets: Record<string, string[]>;
}

export interface GetProcessedDataParams {
  jobId: string;
  filters?: RowFilters;
}

export interface ExtractionHistoryItem {
  id: string;
  jobId: string;
  stores: number[];
  month: number;
  year: number;
  userId: string;
  userEmail: string;
  createdAt: string;
  cacheAvailable: boolean;
}

export interface ExtractionHistoryResponse {
  data: ExtractionHistoryItem[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ExtractionHistoryParams {
  page?: number;
  limit?: number;
  month?: number;
  year?: number;
}

export interface OfmAvailabilityResponse {
  year: number;
  month: number;
  lojasDisponiveis: number[];
  publishedAt: string | null;
  firstPublishedAt: string | null;
  storeCount: number;
}

export type DownloadExcelResponse = BlobDownload | EmptyResponse;

export interface ReprocessRequest {
  year: number;
  month: number;
  lojas?: number[];
  fallback?: "ofm" | "gft";
}

export interface ReprocessResponse {
  mode: "ofm" | "gft";
  id: string;
  alreadyPending?: boolean;
}

export interface ReprocessStatusResponse {
  pending: boolean;
  schedulerId?: string;
  stores?: number[];
}

export interface JobMetaResponse {
  jobId: string;
  month: number | null;
  year: number | null;
  stores: number[];
}

export const taxClosingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    generateTaxClosing: builder.mutation<
      { id: string; lojasFaltando?: number[] },
      CreateTaxClosingRequest
    >({
      query: (body) => ({
        url: "/tax/closings/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.TAX_CLOSING_ACTIVE, Tags.TAX_CLOSING_HISTORY],
    }),

    createTaxClosing: builder.mutation<
      CreateTaxClosingResponse,
      CreateTaxClosingRequest
    >({
      query: (body) => ({
        url: "/tax/closings",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.TAX_CLOSING_ACTIVE, Tags.TAX_CLOSING_HISTORY],
    }),

    getActiveJob: builder.query<ActiveJobResponse, void>({
      query: () => "/tax/closings/active",
      providesTags: [Tags.TAX_CLOSING_ACTIVE],
    }),

    getJobStatus: builder.query<JobStatusResponse, string>({
      query: (jobId) => `/tax/closings/${jobId}/status`,
      providesTags: [Tags.TAX_CLOSING_STATUS],
    }),

    getProcessedData: builder.query<
      ProcessedDataResponse,
      GetProcessedDataParams
    >({
      query: ({ jobId, filters }) => {
        const params = new URLSearchParams();
        if (filters && Object.keys(filters).length > 0) {
          params.set(FILTERS_QUERY_KEY, JSON.stringify(filters));
        }
        const qs = params.toString();
        return `/tax/closings/${jobId}/data${qs ? `?${qs}` : ""}`;
      },
      providesTags: [Tags.TAX_CLOSING],
    }),

    getExtractionHistory: builder.query<
      ExtractionHistoryResponse,
      ExtractionHistoryParams
    >({
      query: ({ page = 1, limit = 20, month, year } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (month) params.set("month", String(month));
        if (year) params.set("year", String(year));
        return `/tax/closings/history?${params.toString()}`;
      },
      providesTags: [Tags.TAX_CLOSING_HISTORY],
    }),

    clearTaxClosingQueue: builder.mutation<
      {
        activeRemoved: number;
        waitingRemoved: number;
        delayedRemoved: number;
      },
      void
    >({
      query: () => ({
        url: "/tax/closings/queue",
        method: "DELETE",
      }),
      invalidatesTags: [
        Tags.TAX_CLOSING_ACTIVE,
        Tags.TAX_CLOSING_STATUS,
        Tags.TAX_CLOSING_HISTORY,
      ],
    }),

    refreshVarTab: builder.mutation<ProcessedDataResponse, string>({
      query: (jobId) => ({
        url: `/tax/closings/${jobId}/refresh-var-tab`,
        method: "POST",
      }),
      invalidatesTags: [Tags.TAX_CLOSING],
    }),

    downloadExcel: builder.mutation<DownloadExcelResponse, string>({
      query: (jobId) => ({
        url: `/tax/closings/${jobId}/download`,
        method: "GET",
        responseHandler: (response: Response): Promise<DownloadExcelResponse> =>
          blobDownloadResponseHandler<EmptyResponse>(response),
      }),
    }),

    getOfmAvailability: builder.query<
      OfmAvailabilityResponse,
      { year: number; month: number }
    >({
      query: ({ year, month }) =>
        `/tax/closings/ofm-availability?year=${year}&month=${month}`,
    }),

    reprocessTaxClosing: builder.mutation<ReprocessResponse, ReprocessRequest>({
      query: (body) => ({
        url: "/tax/closings/reprocess",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        Tags.TAX_CLOSING_ACTIVE,
        Tags.TAX_CLOSING_HISTORY,
        Tags.TAX_CLOSING_REPROCESS,
        Tags.OFM_LIST_SCHEDULER,
      ],
    }),

    getReprocessStatus: builder.query<
      ReprocessStatusResponse,
      { year: number; month: number }
    >({
      query: ({ year, month }) =>
        `/tax/closings/reprocess-status?year=${year}&month=${month}`,
      providesTags: [Tags.TAX_CLOSING_REPROCESS],
    }),

    getJobMeta: builder.query<JobMetaResponse, string>({
      query: (jobId) => `/tax/closings/${jobId}/meta`,
    }),

    getJobReprocessSchedulers: builder.query<
      { items: SchedulerSummary[] },
      string
    >({
      query: (jobId) => `/tax/closings/${jobId}/reprocess-schedulers`,
      providesTags: [Tags.OFM_LIST_SCHEDULER],
    }),

    getTabRows: builder.query<TabRowsResponse, TabRowsParams>({
      query: ({ jobId, tab, page = 1, limit = 20, sort, order, search, filters }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (sort) params.set("sort", sort);
        if (order) params.set("order", order);
        if (search) params.set("search", search);
        if (filters && Object.keys(filters).length > 0) {
          params.set(FILTERS_QUERY_KEY, JSON.stringify(filters));
        }
        return `/tax/closings/${jobId}/rows/${tab}?${params.toString()}`;
      },
      providesTags: [Tags.TAX_CLOSING],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGenerateTaxClosingMutation,
  useCreateTaxClosingMutation,
  useGetActiveJobQuery,
  useLazyGetJobStatusQuery,
  useGetJobStatusQuery,
  useGetProcessedDataQuery,
  useGetExtractionHistoryQuery,
  useClearTaxClosingQueueMutation,
  useRefreshVarTabMutation,
  useDownloadExcelMutation,
  useGetTabRowsQuery,
  useGetOfmAvailabilityQuery,
  useReprocessTaxClosingMutation,
  useGetReprocessStatusQuery,
  useGetJobMetaQuery,
  useGetJobReprocessSchedulersQuery,
} = taxClosingApi;
